/**
 * AdminEstimateEditModal
 *
 * Per-estimate admin tools. Controls the creator's name, email, project
 * name, and MSRP visibility — new per-estimate toggles should be added
 * here and to the whitelist in admin-estimates.php PATCH.
 */

import React, { useEffect, useState } from 'react';
import { Save, Loader2, DollarSign, RotateCcw, Trash2 } from 'lucide-react';
import { updateEstimateAdmin, EstimateAdminPatch } from '../../services/api';

export interface AdminEstimateEditable {
  id: number;
  estimate_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  guid: string;
  show_msrp_to_creator: boolean;
  is_deleted: boolean;
}

interface Props {
  isOpen: boolean;
  estimate: AdminEstimateEditable | null;
  onClose: () => void;
  onSaved: (next: AdminEstimateEditable) => void;
}

// Normalize text-field input to the shape we compare against / send.
// Empty string becomes null so the row is cleared rather than storing "".
const normText = (v: string): string | null => {
  const t = v.trim();
  return t === '' ? null : t;
};

export const AdminEstimateEditModal: React.FC<Props> = ({
  isOpen,
  estimate,
  onClose,
  onSaved,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [estimateName, setEstimateName] = useState('');
  const [showMsrp, setShowMsrp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fade in/out state — stays mounted during close animation
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen && estimate) {
      setFirstName(estimate.first_name ?? '');
      setLastName(estimate.last_name ?? '');
      setEmail(estimate.email ?? '');
      setEstimateName(estimate.estimate_name ?? '');
      setShowMsrp(estimate.show_msrp_to_creator);
      setError(null);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, estimate]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (mounted) {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mounted]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!mounted || !estimate) return null;

  const nextFirst = normText(firstName);
  const nextLast = normText(lastName);
  const nextEmail = normText(email);
  const nextEstimateName = normText(estimateName);

  const firstDirty = nextFirst !== estimate.first_name;
  const lastDirty = nextLast !== estimate.last_name;
  const emailDirty = nextEmail !== estimate.email;
  const nameDirty = nextEstimateName !== estimate.estimate_name;
  const msrpDirty = showMsrp !== estimate.show_msrp_to_creator;
  const dirty = firstDirty || lastDirty || emailDirty || nameDirty || msrpDirty;

  const handleSave = async () => {
    if (!dirty) {
      onClose();
      return;
    }
    // Client-side guard — server validates too.
    if (nextEmail !== null && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSaving(true);
    setError(null);

    const patch: EstimateAdminPatch = {};
    if (firstDirty) patch.firstName = nextFirst;
    if (lastDirty) patch.lastName = nextLast;
    if (emailDirty) patch.email = nextEmail;
    if (nameDirty) patch.estimateName = nextEstimateName;
    if (msrpDirty) patch.showMsrpToCreator = showMsrp;

    try {
      const next = await updateEstimateAdmin(estimate.id, patch);
      onSaved({
        ...estimate,
        first_name: next.firstName,
        last_name: next.lastName,
        email: next.email,
        estimate_name: next.estimateName,
        show_msrp_to_creator: next.showMsrpToCreator,
        is_deleted: next.isDeleted,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Admin recovery: clear the creator's soft-delete so the estimate is live
  // again (re-listed for the owner, share link works). Independent of the
  // field-edit save above.
  const handleRestore = async () => {
    if (!estimate) return;
    setRestoring(true);
    setError(null);
    try {
      const next = await updateEstimateAdmin(estimate.id, { restore: true });
      onSaved({ ...estimate, is_deleted: next.isDeleted });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      className={`modal d-block fade ${visible ? 'show' : ''}`}
      tabIndex={-1}
      style={{
        transition: 'opacity 0.3s ease',
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              Edit Estimate <span className="text-muted">#{estimate.id}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {estimate.is_deleted && (
              <div className="alert alert-warning d-flex align-items-start gap-2 py-2">
                <Trash2 size={18} className="mt-1 flex-shrink-0" />
                <div className="flex-grow-1">
                  <div className="fw-semibold small">This estimate was deleted by its creator.</div>
                  <p className="small mb-2">
                    It’s hidden from the creator and its share link is disabled, but it still
                    counts in analytics. Restore it to make it live again.
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm btn-warning d-inline-flex align-items-center gap-1"
                    onClick={handleRestore}
                    disabled={restoring || saving}
                  >
                    {restoring
                      ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Restoring…</>
                      : <><RotateCcw size={14} /> Restore estimate</>}
                  </button>
                </div>
              </div>
            )}

            <div className="row g-2 mb-3">
              <div className="col-sm-6">
                <label htmlFor="edit-first-name" className="form-label small fw-semibold mb-1">
                  First Name
                </label>
                <input
                  id="edit-first-name"
                  type="text"
                  className="form-control form-control-sm"
                  value={firstName}
                  disabled={saving}
                  maxLength={100}
                  onChange={e => setFirstName(e.target.value)}
                />
              </div>
              <div className="col-sm-6">
                <label htmlFor="edit-last-name" className="form-label small fw-semibold mb-1">
                  Last Name
                </label>
                <input
                  id="edit-last-name"
                  type="text"
                  className="form-control form-control-sm"
                  value={lastName}
                  disabled={saving}
                  maxLength={100}
                  onChange={e => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="edit-email" className="form-label small fw-semibold mb-1">
                Email
              </label>
              <input
                id="edit-email"
                type="email"
                className="form-control form-control-sm"
                value={email}
                disabled={saving}
                maxLength={255}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="edit-estimate-name" className="form-label small fw-semibold mb-1">
                Project Name
              </label>
              <input
                id="edit-estimate-name"
                type="text"
                className="form-control form-control-sm"
                value={estimateName}
                disabled={saving}
                maxLength={255}
                onChange={e => setEstimateName(e.target.value)}
              />
            </div>

            <div className="border rounded-3 p-3 mb-0">
              <div className="d-flex align-items-start gap-2">
                <DollarSign size={18} className="text-success mt-1" />
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <label htmlFor="edit-show-msrp" className="fw-semibold m-0">
                      Show MSRP pricing to creator
                    </label>
                    <div className="form-check form-switch m-0">
                      <input
                        id="edit-show-msrp"
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        style={{ transform: 'scale(1.3)' }}
                        checked={showMsrp}
                        disabled={saving}
                        onChange={e => setShowMsrp(e.target.checked)}
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0 mt-1">
                    When off (default), the shared estimate page hides unit
                    price and line-total columns. Turn on to let this estimate&rsquo;s
                    creator see MSRP.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer bg-light">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-1"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving…
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEstimateEditModal;
